import { useQueryClient } from '@tanstack/react-query'
import {
  bffCrmPersonMergeInto,
  bffCrmVerifiedPersons,
  providersUserPeople,
} from '@fireflyhealth/api'
import type {
  BffCrmPersonMergeIntoCreateResponse,
  BffCrmVerifiedPersonsRetrieveQueryParams,
  BffCrmVerifiedPersonsRetrieveResponse,
} from '@fireflyhealth/api'

import { useMutation, useQuery } from '~/shared/hooks/query'
import { required } from '~/shared/lib/required'
// The verified-person search returns an array of rows. The OpenAPI generator
// only captures the item schema for a BFF loader (`serializer_loader_response`
// is single-object; the view renders it with `many=True`), so the generated
// response type is one row rather than a list. Model the real runtime shape
// here; `validateResponse` only warns, so the array reaches us intact.
export type VerifiedPerson = BffCrmVerifiedPersonsRetrieveResponse
export type VerifiedPersonSearchParams = BffCrmVerifiedPersonsRetrieveQueryParams
type PersonMergeResult = BffCrmPersonMergeIntoCreateResponse

const verifiedPersonSearchQueryKey = (params: VerifiedPersonSearchParams | null) =>
  ['verifiedPersonSearch', params] as const

/**
 * Stage 1 of the ops merge tool: search the verified Person pool for a merge
 * destination. Disabled until `params` is set (ops clicks Search); the query
 * re-runs whenever the submitted filters change. Non-filter identity fields
 * come back masked (`"••••"`) from the server.
 */
export const useVerifiedPersonSearch = (params: VerifiedPersonSearchParams | null) =>
  useQuery<VerifiedPerson[]>(
    verifiedPersonSearchQueryKey(params),
    () =>
      bffCrmVerifiedPersons
        .retrieve({ params: required(params, 'search params') })
        // See the note on VerifiedPerson: the runtime body is an array.
        .then(r => r.data as unknown as VerifiedPerson[]),
    { enabled: !!params },
    { error: 'Verified-account search failed' },
  )

interface MergePersonIntoInput {
  destinationPersonId: number
  firstName: string
  lastName: string
  dob: string
  memberId?: string
  streetAddress?: string
  city?: string
  zipcode?: string
  state?: string
  isConfirmedByOps: boolean
}

/**
 * Stage 2 of the ops merge tool: merge the unknown (placeholder) source Person
 * into a verified destination. `validated_by` is derived server-side from the
 * request user — never sent from here. On success the source is soft-deleted and
 * its cases / tasks / phone calls move to the surviving destination, so we
 * invalidate the destination's person cache and let the caller redirect.
 */
export const useMergePersonInto = (sourcePersonId: number) => {
  const client = useQueryClient()
  return useMutation<PersonMergeResult, unknown, MergePersonIntoInput>(
    ({
      destinationPersonId,
      firstName,
      lastName,
      dob,
      memberId,
      streetAddress,
      city,
      zipcode,
      state,
      isConfirmedByOps,
    }) =>
      bffCrmPersonMergeInto
        .create(
          { source_person_id: sourcePersonId },
          {
            data: {
              destination_person_id: destinationPersonId,
              first_name: firstName,
              last_name: lastName,
              dob,
              member_id: memberId,
              street_address: streetAddress,
              city,
              zipcode,
              state,
              is_confirmed_by_ops: isConfirmedByOps,
            },
          },
        )
        .then(r => r.data),
    {
      onSuccess: async result => {
        await client.invalidateQueries({
          queryKey: providersUserPeople.retrieveQueryKey({
            id: result.destination_person_id,
            provider_id: 'me',
          }),
        })
      },
    },
  )
}
